<template>
	<div class="cms-gallery" :class="['cms-gallery--' + layoutMode, 'cms-gallery--aspect-' + aspect]" :style="rootVars">
		<template v-if="images && images.length">
			<!-- grid: equal tiles · masonry: natural heights in columns -->
			<div v-if="layoutMode === 'grid' || layoutMode === 'masonry'" class="cms-gallery__items">
				<figure v-for="(img, idx) in images" :key="'gal-' + idx" class="cms-gallery__item" @click="lightboxEnabled && openLightbox(idx)">
					<img v-bind="tileAttrs(img)" :alt="img.alt || ''" class="cms-gallery__img" />
					<figcaption v-if="captionsOn && (img.caption || creditFor(img))" class="cms-gallery__caption"><span v-if="img.caption">{{ img.caption }}</span><small v-if="creditFor(img)" class="cms-gallery__credit">{{ creditFor(img) }}</small></figcaption>
				</figure>
			</div>

			<!-- carousel: horizontal scroll with snap; swipe on touch, arrows on desktop -->
			<div v-else-if="layoutMode === 'carousel'" class="cms-gallery__carousel">
				<button v-if="images.length > 1" type="button" class="cms-gallery__arrow cms-gallery__arrow--prev" aria-label="Previous" @click="scrollBy(-1)"><i class="fa fa-chevron-left"></i></button>
				<div ref="track" class="cms-gallery__track">
					<figure v-for="(img, idx) in images" :key="'gal-' + idx" class="cms-gallery__item cms-gallery__item--slide" @click="lightboxEnabled && openLightbox(idx)">
						<img v-bind="tileAttrs(img)" :alt="img.alt || ''" class="cms-gallery__img" />
						<figcaption v-if="captionsOn && (img.caption || creditFor(img))" class="cms-gallery__caption"><span v-if="img.caption">{{ img.caption }}</span><small v-if="creditFor(img)" class="cms-gallery__credit">{{ creditFor(img) }}</small></figcaption>
					</figure>
				</div>
				<button v-if="images.length > 1" type="button" class="cms-gallery__arrow cms-gallery__arrow--next" aria-label="Next" @click="scrollBy(1)"><i class="fa fa-chevron-right"></i></button>
			</div>

			<!-- slideshow: one image at a time, arrows + dots, swipe on touch -->
			<div v-else class="cms-gallery__show" @touchstart.passive="touchStart" @touchend.passive="touchEnd">
				<figure class="cms-gallery__item cms-gallery__item--show" @click="lightboxEnabled && openLightbox(current)">
					<img v-bind="slideAttrs(images[current])" :alt="images[current].alt || ''" class="cms-gallery__img" />
					<figcaption v-if="captionsOn && (images[current].caption || creditFor(images[current]))" class="cms-gallery__caption cms-gallery__caption--overlay"><span v-if="images[current].caption">{{ images[current].caption }}</span><small v-if="creditFor(images[current])" class="cms-gallery__credit">{{ creditFor(images[current]) }}</small></figcaption>
				</figure>
				<template v-if="images.length > 1">
					<button type="button" class="cms-gallery__arrow cms-gallery__arrow--prev" aria-label="Previous" @click.stop="step(-1)"><i class="fa fa-chevron-left"></i></button>
					<button type="button" class="cms-gallery__arrow cms-gallery__arrow--next" aria-label="Next" @click.stop="step(1)"><i class="fa fa-chevron-right"></i></button>
					<div class="cms-gallery__dots">
						<button v-for="(img, idx) in images" :key="'dot-' + idx" type="button" class="cms-gallery__dot" :class="{ 'cms-gallery__dot--on': idx === current }" :aria-label="'Image ' + (idx + 1)" @click.stop="current = idx"></button>
					</div>
				</template>
			</div>
		</template>
		<div v-else class="cms-gallery__empty">
			<i class="fa fa-th"></i>
			<span>No images added yet</span>
		</div>

		<!-- Lightbox overlay -->
		<div v-if="lightboxIndex !== null" class="cms-gallery__lightbox" @click.self="lightboxIndex = null">
			<button class="cms-gallery__lightbox-close" @click="lightboxIndex = null">
				<i class="fa fa-times"></i>
			</button>
			<button v-if="lightboxIndex > 0" class="cms-gallery__lightbox-nav cms-gallery__lightbox-nav--prev" @click.stop="lightboxIndex--">
				<i class="fa fa-chevron-left"></i>
			</button>
			<img
				v-bind="slideAttrs(images[lightboxIndex], true)"
				:alt="images[lightboxIndex].alt || ''"
				class="cms-gallery__lightbox-img"
			/>
			<button v-if="lightboxIndex < images.length - 1" class="cms-gallery__lightbox-nav cms-gallery__lightbox-nav--next" @click.stop="lightboxIndex++">
				<i class="fa fa-chevron-right"></i>
			</button>
		</div>
	</div>
</template>

<script>
/**
 * ImageGalleryBlock.vue
 * ─────────────────────
 * Images in one of four layouts — grid (equal tiles), masonry (natural
 * heights), carousel (horizontal swipe/scroll with arrows) or slideshow
 * (one at a time with dots) — with optional captions and a lightbox.
 * Tile shape comes from `aspect`; outer chrome from the wrapper traits.
 */
import { coerceBool } from '@/websites/cms/blocks/shared/blockUtils.js';
import { creditLine } from '../shared/imageCredit.js';

import { imgAttrs } from '../shared/imageSrc.js';

export default {
	name: 'ImageGalleryBlock',

	inject: {
		siteSettings: { default: function () { return {}; } }
	},

	props: {
		images: {
			type: Array,
			default: function () { return []; }
		},
		columns: {
			type: [Number, String],
			default: 3
		},
		gap: {
			type: String,
			default: '0.5rem'
		},
		borderRadius: {
			type: String,
			default: '4px'
		},
		lightbox: {
			type: [Boolean, String],
			default: true
		},
		layout: { type: String, default: 'grid' },
		aspect: { type: String, default: 'square' },
		showCaptions: { type: [Boolean, String], default: true }
	},

	data: function () {
		return {
			lightboxIndex: null,
			current: 0,
			touchX: null
		};
	},

	computed: {
		lightboxEnabled: function () {
			return coerceBool(this.lightbox);
		},
		captionsOn: function () {
			return coerceBool(this.showCaptions);
		},
		layoutMode: function () {
			return ['grid', 'masonry', 'carousel', 'slideshow'].indexOf(this.layout) !== -1 ? this.layout : 'grid';
		},
		rootVars: function () {
			return {
				'--gal-cols': String(parseInt(this.columns, 10) || 3),
				'--gal-gap': this.gap || '0.5rem',
				'--gal-radius': this.borderRadius || 'var(--site-image-radius, 4px)',
				'--gal-cursor': this.lightboxEnabled ? 'pointer' : 'default'
			};
		}
	},

	watch: {
		images: function (list) {
			if (this.current >= (list || []).length) this.current = 0;
		}
	},

	methods: {
		creditFor: function (img) { return creditLine(img && img.credit, this.siteSettings, img && img.showCredit); },

		/** Grid/masonry tile: sized for its column share. */
		tileAttrs: function (img) {
			var cols = Math.max(1, parseInt(this.columns, 10) || 3);
			return imgAttrs(img.src, { sizes: '(max-width: 768px) 50vw, ' + Math.round(100 / cols) + 'vw', fallbackWidth: 640 });
		},
		/** Carousel slide / slideshow / lightbox: close to the full width. */
		slideAttrs: function (img, eager) {
			return imgAttrs(img.src, { sizes: '100vw', priority: !!eager });
		},

		openLightbox: function (idx) {
			this.lightboxIndex = idx;
		},

		step: function (dir) {
			var n = this.images.length;
			this.current = (this.current + dir + n) % n;
		},

		scrollBy: function (dir) {
			var track = this.$refs.track;
			if (!track) return;
			var slide = track.querySelector('.cms-gallery__item--slide');
			var width = slide ? slide.getBoundingClientRect().width : track.clientWidth * 0.8;
			track.scrollBy({ left: dir * (width + 12), behavior: 'smooth' });
		},

		touchStart: function (e) { this.touchX = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : null; },
		touchEnd: function (e) {
			if (this.touchX === null || !e.changedTouches || !e.changedTouches[0]) return;
			var dx = e.changedTouches[0].clientX - this.touchX;
			this.touchX = null;
			if (Math.abs(dx) > 40) this.step(dx < 0 ? 1 : -1);
		}
	}
};
</script>

<style scoped>
.cms-gallery { width: 100%; position: relative; }
.cms-gallery__item { margin: 0; overflow: hidden; cursor: var(--gal-cursor, default); break-inside: avoid; }
.cms-gallery__img {
	width: 100%;
	display: block;
	object-fit: cover;
	border-radius: var(--gal-radius);
	transition: transform 0.2s, opacity 0.2s;
}
.cms-gallery__item:hover .cms-gallery__img { transform: scale(1.02); opacity: 0.95; }
.cms-gallery__caption { font-size: .9em; color: var(--site-body-color, inherit); opacity: .75; margin-top: 0.35rem; text-align: center; display: flex; flex-direction: column; gap: .1rem; }
.cms-gallery__credit { font-size: .78em; letter-spacing: .02em; }

/* tile shape */
.cms-gallery--aspect-square .cms-gallery__img { aspect-ratio: 1; }
.cms-gallery--aspect-landscape .cms-gallery__img { aspect-ratio: 4 / 3; }
.cms-gallery--aspect-wide .cms-gallery__img { aspect-ratio: 16 / 9; }
.cms-gallery--aspect-natural .cms-gallery__img { aspect-ratio: auto; height: auto; }

/* grid */
.cms-gallery--grid .cms-gallery__items { display: grid; grid-template-columns: repeat(var(--gal-cols, 3), minmax(0, 1fr)); gap: var(--gal-gap); }

/* masonry: CSS columns, natural heights */
.cms-gallery--masonry .cms-gallery__items { column-count: var(--gal-cols, 3); column-gap: var(--gal-gap); }
.cms-gallery--masonry .cms-gallery__item { margin-bottom: var(--gal-gap); display: inline-block; width: 100%; }
.cms-gallery--masonry .cms-gallery__img { aspect-ratio: auto; height: auto; }

/* carousel: scroll-snap track, arrows */
.cms-gallery__carousel { position: relative; }
.cms-gallery__track {
	display: flex; gap: var(--gal-gap); overflow-x: auto; scroll-snap-type: x mandatory;
	scrollbar-width: none; -webkit-overflow-scrolling: touch; padding-bottom: 4px;
}
.cms-gallery__track::-webkit-scrollbar { display: none; }
.cms-gallery__item--slide { flex: 0 0 calc((100% - (var(--gal-cols, 3) - 1) * var(--gal-gap)) / var(--gal-cols, 3)); scroll-snap-align: start; }
.cms-gallery__arrow {
	position: absolute; top: 50%; transform: translateY(-50%); z-index: 2;
	width: 40px; height: 40px; border-radius: 50%; border: 0; cursor: pointer;
	background: rgba(255, 255, 255, .92); color: #1E2031; box-shadow: 0 2px 10px rgba(0, 0, 0, .18);
	display: inline-flex; align-items: center; justify-content: center;
}
.cms-gallery__arrow--prev { left: 8px; }
.cms-gallery__arrow--next { right: 8px; }
.cms-gallery__arrow:hover { background: #FFF; }

/* slideshow */
.cms-gallery__show { position: relative; }
.cms-gallery__item--show { position: relative; }
.cms-gallery__caption--overlay {
	position: absolute; left: 0; right: 0; bottom: 0; margin: 0; padding: .75rem 1rem; text-align: left;
	color: #FFF; opacity: 1; background: linear-gradient(transparent, rgba(0, 0, 0, .6));
	border-radius: 0 0 var(--gal-radius) var(--gal-radius);
}
.cms-gallery__dots { display: flex; justify-content: center; gap: 6px; margin-top: .6rem; }
.cms-gallery__dot { width: 8px; height: 8px; border-radius: 50%; border: 0; padding: 0; cursor: pointer; background: var(--site-hr-color, #DDDEE0); }
.cms-gallery__dot--on { background: var(--site-accent-color, #FB6A00); }

@media (max-width: 768px) {
	.cms-gallery--grid .cms-gallery__items { grid-template-columns: repeat(2, minmax(0, 1fr)); }
	.cms-gallery--masonry .cms-gallery__items { column-count: 2; }
	.cms-gallery__item--slide { flex-basis: 80%; }
}

.cms-gallery__empty {
	grid-column: 1 / -1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 120px;
	background: #F4F4F5;
	border: 2px dashed #DDDEE0;
	border-radius: 4px;
	color: #999;
	gap: 0.5rem;
}
.cms-gallery__empty .fa {
	font-size: 2em;
}

/* ── Lightbox ─────────────────────────────────────────── */
.cms-gallery__lightbox {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(0, 0, 0, 0.88);
	z-index: 9999;
	display: flex;
	align-items: center;
	justify-content: center;
}
.cms-gallery__lightbox-img {
	max-width: 90vw;
	max-height: 85vh;
	border-radius: 4px;
	box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5);
}
.cms-gallery__lightbox-close {
	position: absolute;
	top: 1rem;
	right: 1rem;
	width: 40px;
	height: 40px;
	border: none;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.15);
	color: #FFF;
	font-size: 1.2em;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background 0.15s;
}
.cms-gallery__lightbox-close:hover {
	background: rgba(255, 255, 255, 0.3);
}
.cms-gallery__lightbox-nav {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	width: 44px;
	height: 44px;
	border: none;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.15);
	color: #FFF;
	font-size: 1.2em;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background 0.15s;
}
.cms-gallery__lightbox-nav:hover {
	background: rgba(255, 255, 255, 0.3);
}
.cms-gallery__lightbox-nav--prev {
	left: 1rem;
}
.cms-gallery__lightbox-nav--next {
	right: 1rem;
}
</style>
